import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@utils/api';
import { setCookie, getCookie, removeCookie } from '@utils/cookies';
import { getSocket, disconnectSocket } from '@utils/socket';
import { usePublicConfig } from '@context/PublicConfigContext';
import { SOCKET_EVENTS } from '../../../shared/constants';
import { isAuthSurfacePath, unauthenticatedEntryPath } from '@utils/authEntryPaths';

/** Session cookie lifetime (must stay in sync with server JWT expiry). */
const TOKEN_COOKIE_DAYS = 365;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getCookie('pf_token'));
  const [loading, setLoading] = useState(true);
  const { ready: publicConfigReady } = usePublicConfig();

  const loadUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch (error) {
      const status = error.response?.status;
      const code = error.response?.data?.code;
      const forcedLogout =
        (status === 403 && code === 'ACCOUNT_BLOCKED') ||
        (status === 401 && code === 'SESSION_REVOKED');
      if (forcedLogout) {
        removeCookie('pf_token');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (token && publicConfigReady) {
      getSocket(token);
    }
  }, [token, publicConfigReady]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setCookie('pf_token', data.token, TOKEN_COOKIE_DAYS);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const googleLogin = async (credential) => {
    const { data } = await api.post('/auth/google', { credential });
    setCookie('pf_token', data.token, TOKEN_COOKIE_DAYS);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const googleLoginWithCode = async (code) => {
    const { data } = await api.post('/auth/google', { code });
    setCookie('pf_token', data.token, TOKEN_COOKIE_DAYS);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password, interests) => {
    const { data } = await api.post('/auth/register', { name, email, password, interests });
    setCookie('pf_token', data.token, TOKEN_COOKIE_DAYS);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = useCallback(() => {
    removeCookie('pf_token');
    setToken(null);
    setUser(null);
    disconnectSocket();
  }, []);

  useEffect(() => {
    if (!token || !publicConfigReady) return undefined;
    const socket = getSocket(token);
    if (!socket) return undefined;
    const onSessionRevoked = () => {
      removeCookie('pf_token');
      setToken(null);
      setUser(null);
      disconnectSocket();
      if (!isAuthSurfacePath(window.location.pathname)) {
        window.location.href = unauthenticatedEntryPath();
      }
    };
    socket.on(SOCKET_EVENTS.SESSION_REVOKED, onSessionRevoked);
    return () => {
      socket.off(SOCKET_EVENTS.SESSION_REVOKED, onSessionRevoked);
    };
  }, [token, publicConfigReady]);

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        googleLogin,
        googleLoginWithCode,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
